import { createSelector } from '@reduxjs/toolkit';

import { deriveStatus, isCleared, isRunning } from '@/components/Tours/core/status';
import type { TourAnalytics, TourGateState, TourState, TourStatus } from '@/components/Tours/types';
import type { AppState } from '@/redux/helper';

/**
 * Client progress (the history stack) with the server-owned analytics from the user object
 * overlaid on top. There is no hydrate action — the user object already lands in redux, so the
 * two are merged at read time. The single definition of "the effective tour state".
 */
/** Later of two ISO timestamps. Safe as a string compare — ISO 8601 UTC sorts chronologically. */
const latest = (a?: string, b?: string): string | undefined => {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
};

/**
 * Has the client begun a run the server has not seen? `startedAt` identifies the current run,
 * so a newer one means the client's per-run fields are the truth — including the CLEARS that
 * restartTour performs.
 */
const clientHasNewerRun = (client?: TourState, analytics?: TourAnalytics): boolean => {
  if (!client?.startedAt) return false;
  if (!analytics?.startedAt) return true;
  return client.startedAt > analytics.startedAt;
};

const mergeTourState = (client?: TourState, analytics?: TourAnalytics): TourState => {
  const merged: TourState = {
    stepHistory: [],
    completedTimes: 0,
    ...client,
    ...analytics, // server is authoritative for lifecycle timestamps
  };

  // Cross-run fields only ever accumulate, so take the high-water value rather than letting a
  // not-yet-synced server value pull them backwards.
  merged.completedTimes = Math.max(client?.completedTimes ?? 0, analytics?.completedTimes ?? 0);
  merged.lastStartedAt = latest(client?.lastStartedAt, analytics?.lastStartedAt);
  merged.lastCompletedAt = latest(client?.lastCompletedAt, analytics?.lastCompletedAt);

  if (clientHasNewerRun(client, analytics)) {
    // Per-run fields come from the client wholesale. Field-by-field "server wins" would let a
    // stale completedAt survive a restart, and the tour would immediately read as completed
    // again — the restart would be inert the moment sync is live.
    merged.startedAt = client?.startedAt;
    merged.completedAt = client?.completedAt;
    merged.skippedAt = client?.skippedAt;
    merged.furthestStepId = client?.furthestStepId;
    merged.furthestStepNumber = client?.furthestStepNumber;
  } else if ((client?.furthestStepNumber ?? -1) > (analytics?.furthestStepNumber ?? -1)) {
    // Same run on both sides: progress is monotonic.
    merged.furthestStepNumber = client?.furthestStepNumber;
    merged.furthestStepId = client?.furthestStepId;
  }

  if (!clientHasNewerRun(client, analytics)) {
    // Same run: prefer local terminal timestamps when present so skip/complete takes effect
    // before the PATCH lands (deriveStatus / isRunning key off these fields).
    if (client?.completedAt) merged.completedAt = client.completedAt;
    if (client?.skippedAt) merged.skippedAt = client.skippedAt;
  }

  // Position is client-owned: the local history is the authority on where the user is.
  merged.currentStepId = client?.currentStepId ?? analytics?.currentStepId;

  return merged;
};

export const selectTour =
  (tourId: string) =>
  (state: AppState): TourState | undefined => {
    const client = state.tours.tours[tourId];
    const analytics = state.user.tours?.[tourId];
    if (!client && !analytics) return undefined;
    return mergeTourState(client, analytics);
  };

/**
 * The arbiter's entire view of the store, memoized on three inputs. Everything the arbiter and
 * the gate predicates need is here, so they never touch the root state — without this, holding
 * `useSelector((s) => s)` re-runs arbitration on every dispatch in the app.
 *
 * The result identity only changes when tour progress, synced analytics, or subscription
 * features change, so consumers can safely memoize on it.
 */
export const selectTourGate = createSelector(
  (state: AppState) => state.tours.tours,
  (state: AppState) => state.user.tours,
  (state: AppState) => state.user.subscription?.featuresUntil,
  (client, analytics, featuresUntil): TourGateState => {
    const tours: Record<string, TourState> = {};
    new Set([...Object.keys(client), ...Object.keys(analytics ?? {})]).forEach((id) => {
      tours[id] = mergeTourState(client[id], analytics?.[id]);
    });
    return { featuresUntil, tours };
  },
);

/**
 * Whether tours may act at all. Two conditions, and both matter for a different reason:
 *
 * `sessionChecked` — the client-side tour progress is rehydrated from localStorage immediately,
 * but the server-owned analytics in `user.tours` only arrive with the session. Acting before
 * that means arbitrating and syncing against a half-merged view: a tour finished on another
 * device could be auto-started again, and TourSync could push stale local analytics over it.
 *
 * `logged` — tours belong to a user; a guest has no progress to read or write.
 */
export const selectToursReady = (state: AppState): boolean => state.user.sessionChecked && state.user.logged;

export const selectTourStatus =
  (tourId: string) =>
  (state: AppState): TourStatus =>
    deriveStatus(selectTour(tourId)(state));

export const selectCurrentStepId =
  (tourId: string) =>
  (state: AppState): string | undefined => {
    // Current position is client-owned (the history stack lives only in the tours slice).
    const history = state.tours.tours[tourId]?.stepHistory;
    return history?.[history.length - 1];
  };

export const selectIsTourRunning =
  (tourId: string) =>
  (state: AppState): boolean =>
    isRunning(selectTour(tourId)(state));

export const selectIsTourCleared =
  (tourId: string) =>
  (state: AppState): boolean =>
    isCleared(selectTour(tourId)(state));
